import React, { useEffect, useState } from 'react';
import {
  ComposedChart, BarChart, Bar, Cell, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, LabelList,
} from 'recharts';
import { Download } from 'lucide-react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useAppShell } from '../contexts/AppShellContext';
import { getExtractionAccuracy, ExtractionAccuracyData, FieldAccuracy } from '../services/analyticsService';
import {
  ScanDetail,
  mockScansForWeek,
  mockSummary, mockScanTypeBreakdown, mockFieldAccuracy,
} from '../data/mockScanAnalytics';
import { exportOpsReportCsv } from '../utils/exportCsv';
import { exportOpsReportPdf } from '../utils/exportOpsReportPdf';
import { getOpsReportSupplementalData } from '../utils/opsReportData';
import { useNavigate } from 'react-router-dom';
import { useProxyWorkflowContext } from '../hooks/useProxyWorkflowContext';
import {
  buildProxyIntakeTasks, isProxyTaskException, escalationFor, PROXY_WORKFLOW_REGISTRY,
} from '../data/proxyWorkflowRegistry';
import { leadMessage, useEmailInbox } from '../contexts/EmailInboxContext';
import { useProxyData } from '../contexts/ProxyDataContext';
import { EMAIL_CATEGORY_LABEL, type ProxyEmailCategory } from '../utils/proxyEmailClassifier';
import { CATEGORY_LABEL } from '../utils/jobCategory';

const HIGH_COLOR = '#22c55e';
const MID_COLOR  = '#f59e0b';
const LOW_COLOR  = '#ef4444';
const VOL_COLOR  = '#f97316';
const ACC_COLOR  = '#eab308';

const PARALLEL_RUN_START = new Date('2026-07-01');

type FieldCat = 'all' | 'P' | 'CA' | 'R' | 'I';
type PeriodMode = 'day' | 'week' | 'month' | 'custom';
type DashboardMode = 'accuracy' | 'proxy';

const CATEGORY_OPTIONS: { label: string; value: FieldCat }[] = [
  { label: 'All', value: 'all' },
  { label: 'Proxy', value: 'P' },
  { label: 'Corp Action', value: 'CA' },
  { label: 'Regulatory', value: 'R' },
  { label: 'Issuer', value: 'I' },
];

const BREAKDOWN_KEY = { P: 'proxy', CA: 'corp_action', R: 'regulatory', I: 'issuer' } as const;

function fieldBarColor(pct: number) {
  if (pct >= 95) return HIGH_COLOR;
  if (pct >= 80) return MID_COLOR;
  return LOW_COLOR;
}

function accuracyColor(pct: number) {
  if (pct === 100) return HIGH_COLOR;
  if (pct >= 80)  return MID_COLOR;
  return LOW_COLOR;
}

function toLocalIso(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function fmtDate(iso: string) {
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function weekStartLabel(weekNum: number): string {
  const d = new Date('2026-07-01T00:00:00');
  d.setDate(d.getDate() + (weekNum - 1) * 7);
  return fmtDate(toLocalIso(d));
}

function toWeeklyData(days: { date: string; total: number; avg_score_pct: number; high: number; mid: number; low: number }[]) {
  const buckets: Record<number, { date: string; Volume: number; 'Accuracy %': number; High: number; Mid: number; Low: number; _wsum: number; _count: number }> = {};
  for (const d of days) {
    const ms = new Date(d.date).getTime() - PARALLEL_RUN_START.getTime();
    const week = Math.max(1, Math.floor(ms / (7 * 86_400_000)) + 1);
    if (!buckets[week]) {
      buckets[week] = { date: weekStartLabel(week), Volume: 0, 'Accuracy %': 0, High: 0, Mid: 0, Low: 0, _wsum: 0, _count: 0 };
    }
    const b = buckets[week];
    b.Volume += d.total;  b.High += d.high;  b.Mid += d.mid;  b.Low += d.low;
    b._wsum  += d.avg_score_pct * d.total;   b._count += d.total;
  }
  return Object.values(buckets).map(b => ({
    date: b.date, Volume: b.Volume,
    'Accuracy %': b._count ? Math.round(b._wsum / b._count * 10) / 10 : 0,
    High: b.High, Mid: b.Mid, Low: b.Low,
  }));
}

function scansToChartData(scans: ScanDetail[]) {
  const buckets: Record<number, { date: string; Volume: number; 'Accuracy %': number; High: number; Mid: number; Low: number; _wsum: number; _count: number }> = {};
  for (const s of scans) {
    const ms = new Date(s.date + 'T00:00:00').getTime() - PARALLEL_RUN_START.getTime();
    const week = Math.max(1, Math.floor(ms / (7 * 86_400_000)) + 1);
    if (!buckets[week]) {
      buckets[week] = { date: weekStartLabel(week), Volume: 0, 'Accuracy %': 0, High: 0, Mid: 0, Low: 0, _wsum: 0, _count: 0 };
    }
    const b = buckets[week];
    b.Volume++;
    if (s.accuracy_pct === 100) b.High++;
    else if (s.accuracy_pct >= 80) b.Mid++;
    else b.Low++;
    b._wsum += s.accuracy_pct;
    b._count++;
  }
  return Object.values(buckets).map(b => ({
    date: b.date, Volume: b.Volume,
    'Accuracy %': b._count ? Math.round(b._wsum / b._count * 10) / 10 : 0,
    High: b.High, Mid: b.Mid, Low: b.Low,
  }));
}

// ── Small components ────────────────────────────────────────────────────────

function StatCard({ label, value, sub, accent }: {
  label: string; value: string | number; sub?: string; accent?: string;
}) {
  return (
    <div className="bg-white border border-trim rounded-xl px-5 py-4 flex flex-col gap-1 min-w-[160px]">
      <span className="text-xs text-muted uppercase tracking-wide font-medium">{label}</span>
      <span className="text-2xl font-semibold text-navy" style={accent ? { color: accent } : undefined}>{value}</span>
      {sub && <span className="text-xs text-muted">{sub}</span>}
    </div>
  );
}

function CategoryCard({ label, total, avgPct, high, mid, low }: {
  label: string; total: number; avgPct: number; high: number; mid: number; low: number;
}) {
  return (
    <div className="bg-white border border-trim rounded-xl px-5 py-4 flex flex-col gap-2 flex-1">
      <span className="text-xs text-muted uppercase tracking-wide font-medium">{label}</span>
      <div className="flex items-baseline gap-2">
        <span className="text-2xl font-semibold text-navy">{avgPct}%</span>
        <span className="text-xs text-muted">avg · {total} scan{total !== 1 ? 's' : ''}</span>
      </div>
      <div className="flex gap-3 text-xs">
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: HIGH_COLOR }} />{high} perfect</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: MID_COLOR }} />{mid} mid</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: LOW_COLOR }} />{low} low</span>
      </div>
    </div>
  );
}

function AccuracyBadge({ pct }: { pct: number }) {
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold text-white" style={{ background: accuracyColor(pct) }}>
      {pct}%
    </span>
  );
}

function PillGroup<T extends string>({ options, value, onChange }: {
  options: { label: string; value: T }[];
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <div className="flex items-center gap-1 bg-surface border border-trim rounded-lg p-0.5">
      {options.map(opt => (
        <button
          key={opt.value}
          onClick={() => onChange(opt.value)}
          className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${
            value === opt.value
              ? 'bg-white text-navy shadow-sm border border-trim'
              : 'text-muted hover:text-copy'
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

function ScanDetailPanel({ scans, weekLabel }: { scans: ScanDetail[]; weekLabel: string }) {
  const [tab, setTab] = useState<FieldCat>('all');

  const visible = tab === 'all' ? scans : scans.filter(s => s.job_category === tab);
  const MAX_ROWS = 100;
  const capped   = visible.slice(0, MAX_ROWS);
  const overflow = visible.length > MAX_ROWS;

  if (scans.length === 0) return null;

  return (
    <div className="bg-white border border-trim rounded-xl overflow-hidden">
      <div className="px-5 py-3 border-b border-trim flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-navy">Scan Details</h3>
          <p className="text-xs text-muted mt-0.5">{weekLabel} · {visible.length} scan{visible.length !== 1 ? 's' : ''}</p>
        </div>
        <PillGroup options={CATEGORY_OPTIONS} value={tab} onChange={(v) => setTab(v)} />
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-trim bg-surface">
              <th className="text-left px-4 py-2.5 font-medium text-muted w-20">Scan</th>
              <th className="text-left px-4 py-2.5 font-medium text-muted">Client</th>
              <th className="text-center px-4 py-2.5 font-medium text-muted w-20">Date</th>
              <th className="text-center px-4 py-2.5 font-medium text-muted w-28">Accuracy</th>
              <th className="text-center px-4 py-2.5 font-medium text-muted w-20">Score</th>
              <th className="text-left px-4 py-2.5 font-medium text-muted">Corrected Data</th>
            </tr>
          </thead>
          <tbody>
            {capped.map((scan, i) => (
              <tr key={scan.scan_id} className={i % 2 === 0 ? '' : 'bg-surface/50'}>
                <td className="px-4 py-2.5 font-mono font-medium text-navy">{scan.scan_id}</td>
                <td className="px-4 py-2.5 text-copy">{scan.client_name}</td>
                <td className="px-4 py-2.5 text-center text-muted">{fmtDate(scan.date)}</td>
                <td className="px-4 py-2.5 text-center"><AccuracyBadge pct={scan.accuracy_pct} /></td>
                <td className="px-4 py-2.5 text-center text-muted">{scan.fields_correct}/{scan.fields_total}</td>
                <td className="px-4 py-2.5">
                  {scan.issues.length === 0 ? (
                    <span className="text-muted italic">—</span>
                  ) : (
                    <div className="flex flex-wrap gap-1">
                      {scan.issues.map(issue => (
                        <span key={issue} className="px-1.5 py-0.5 rounded text-xs bg-red-50 text-red-600 border border-red-100">
                          {issue}
                        </span>
                      ))}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {overflow && (
        <div className="px-5 py-2.5 border-t border-trim text-xs text-muted text-center">
          Showing {MAX_ROWS} of {visible.length} — select a specific week to see all
        </div>
      )}
    </div>
  );
}

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-trim rounded-lg shadow-md px-3 py-2 text-xs">
      <p className="font-semibold text-navy mb-1">{label}</p>
      {payload.map((p: any) => (
        <div key={p.name} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: p.color }} />
          <span className="text-muted">{p.name}:</span>
          <span className="font-medium text-navy">{p.name === 'Accuracy %' ? `${p.value}%` : p.value}</span>
        </div>
      ))}
    </div>
  );
};

// ── Page ────────────────────────────────────────────────────────────────────

export function ExtractionAccuracyPage() {
  const { setTopbar } = useAppShell();
  const [data, setData]         = useState<ExtractionAccuracyData | null>(null);
  const [loading, setLoading]   = useState(true);
  const [dateFrom, setDateFrom] = useState<string | null>(null);
  const [dateTo,   setDateTo]   = useState<string | null>(null);
  const [fieldCat, setFieldCat] = useState<FieldCat>('all');
  const [periodMode, setPeriodMode] = useState<PeriodMode>('custom');
  const [mode, setMode] = useState<DashboardMode>('accuracy');
  const proxyCtx = useProxyWorkflowContext();
  const { threads } = useEmailInbox();
  const { approvals } = useProxyData();
  const navigate = useNavigate();

  // Data fetch stays independent of `mode` so toggling to the Proxy Collections
  // tab and back never re-triggers the real API call or resets `loading`.
  useEffect(() => {
    getExtractionAccuracy()
      .then(setData)
      .catch(() => { /* backend unavailable — mock data renders */ })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    setTopbar(
      mode === 'accuracy' ? 'Fulfillment Governance' : 'Proxy Governance',
      mode === 'accuracy' ? 'Field-level AI extraction accuracy — parallel run' : 'How accurately Ace classifies and routes the Proxy mailbox, and where a person stepped in',
    );
  }, [mode, setTopbar]);

  const hasRealData = (data?.days.length ?? 0) > 0;
  const isMock = !loading && !hasRealData;

  // Scans filtered by the selected date range (mock path)
  const weekScans = isMock
    ? mockScansForWeek(null).filter(s =>
        (!dateFrom || s.date >= dateFrom) && (!dateTo || s.date <= dateTo)
      )
    : [];

  // Further filtered by global category selection
  const categoryScans = fieldCat === 'all'
    ? weekScans
    : weekScans.filter(s => s.job_category === fieldCat);

  const summary   = isMock ? mockSummary(categoryScans)          : data?.summary ?? { total_receipts: 0, overall_avg_score_pct: 0 };
  const breakdown = isMock ? mockScanTypeBreakdown(categoryScans) : data?.scan_type_breakdown;

  const combinedBreakdown = breakdown ? (() => {
    const sides = Object.values(breakdown);
    const sum = (pick: (s: typeof sides[number]) => number) => sides.reduce((n, s) => n + pick(s), 0);
    const total = sum(s => s.total);
    return {
      total,
      high: sum(s => s.high),
      mid:  sum(s => s.mid),
      low:  sum(s => s.low),
      avg_score_pct: total > 0 ? Math.round(sum(s => s.avg_score_pct * s.total) / total * 10) / 10 : 0,
    };
  })() : null;

  const fieldAcc: (FieldAccuracy & { label: string })[] = isMock
    ? mockFieldAccuracy(categoryScans).map((f: FieldAccuracy) => ({ ...f, label: f.field }))
    : (data?.field_accuracy ?? []).map((f: FieldAccuracy) => ({ ...f, label: f.field }));

  const chartData = hasRealData
    ? toWeeklyData(data!.days.filter(d => (!dateFrom || d.date >= dateFrom) && (!dateTo || d.date <= dateTo)))
    : scansToChartData(categoryScans);

  const periodLabel = (() => {
    const fmt = (d: string) =>
      new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    if (dateFrom && dateTo) return `${fmt(dateFrom)} – ${fmt(dateTo)}`;
    if (dateFrom) return `From ${fmt(dateFrom)}`;
    if (dateTo)   return `Through ${fmt(dateTo)}`;
    return 'since Jul 1, 2026';
  })();

  const buildOpsReportData = async () => {
    const { floorControl, auditTrail } = await getOpsReportSupplementalData();
    return { period: periodLabel, isMock, summary, breakdown, fieldAcc, weekScans: isMock ? weekScans : [], floorControl, auditTrail };
  };

  const handleExport = () => {
    buildOpsReportData().then(exportOpsReportPdf).catch(console.error);
  };

  const handleExportCsv = () => {
    buildOpsReportData().then(exportOpsReportCsv).catch(console.error);
  };

  const setPeriodPreset = (mode: PeriodMode) => {
    setPeriodMode(mode);
    const today = new Date();
    if (mode === 'day') {
      const iso = toLocalIso(today);
      setDateFrom(iso);
      setDateTo(iso);
    } else if (mode === 'week') {
      const start = new Date(today);
      start.setDate(start.getDate() - 6);
      setDateFrom(toLocalIso(start));
      setDateTo(toLocalIso(today));
    } else if (mode === 'month') {
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      setDateFrom(toLocalIso(start));
      setDateTo(toLocalIso(today));
    }
  };

  const fullyAutomatedPct = combinedBreakdown && combinedBreakdown.total > 0
    ? Math.round((combinedBreakdown.high / combinedBreakdown.total) * 100)
    : 0;

  // ── Proxy governance: how often a person had to override Ace. ──
  // Classification accuracy counts an email as right unless a person changed
  // the category Ace gave it. Emails Ace couldn't classify are a separate
  // number: Ace asked for help there rather than getting it wrong.
  const inboundThreads = threads.filter(t => t.origin === 'proxy' && t.messages.some(m => m.direction === 'inbound'));
  const aceClassified = inboundThreads.filter(t => t.aceCategory);
  const changedByPerson = aceClassified.filter(t => t.correction);
  const unclassified = inboundThreads.filter(t => !t.aceCategory);
  const triagedByPerson = unclassified.filter(t => t.correction);
  const classificationPct = aceClassified.length
    ? Math.round(((aceClassified.length - changedByPerson.length) / aceClassified.length) * 1000) / 10
    : 0;

  const accuracyByCategory = (() => {
    const rows: Record<string, { category: ProxyEmailCategory; total: number; changed: number }> = {};
    for (const t of aceClassified) {
      const row = (rows[t.aceCategory!] ??= { category: t.aceCategory!, total: 0, changed: 0 });
      row.total++;
      if (t.correction) row.changed++;
    }
    return Object.values(rows)
      .map(r => ({ ...r, pct: Math.round(((r.total - r.changed) / r.total) * 1000) / 10 }))
      .sort((a, b) => a.pct - b.pct || b.total - a.total);
  })();

  const corrections = threads
    .filter(t => t.correction)
    .sort((a, b) => b.correction!.at.localeCompare(a.correction!.at));

  // Every open task lands in one place: ready for a person to approve as
  // drafted, an exception Ace can't act on, or escalated to the ICT manager.
  const proxyTasks = buildProxyIntakeTasks(proxyCtx);
  const taskBuckets = (() => {
    let ready = 0, escalatedByAce = 0, escalatedByPerson = 0;
    const exceptions: Record<string, number> = {};
    for (const task of proxyTasks) {
      const job = proxyCtx.jobs.find(j => j.jobId === task.jobId);
      if (!job) continue;
      const escalation = escalationFor(task, job, proxyCtx.emails);
      if (escalation?.by === 'Ace') escalatedByAce++;
      else if (escalation) escalatedByPerson++;
      if (isProxyTaskException(task, job)) {
        const label = PROXY_WORKFLOW_REGISTRY[task.workflowType].label;
        exceptions[label] = (exceptions[label] || 0) + 1;
      } else if (!escalation) ready++;
    }
    const exceptionRows = Object.entries(exceptions).sort((a, b) => b[1] - a[1]);
    return {
      ready, escalatedByAce, escalatedByPerson, exceptionRows,
      exceptionTotal: exceptionRows.reduce((n, [, c]) => n + c, 0),
    };
  })();
  const escalatedTotal = taskBuckets.escalatedByAce + taskBuckets.escalatedByPerson;

  // Approved as drafted: the person signed off without changing anything
  // Ace prepared (fields, recipients or email text).
  const approvedAsDrafted = approvals.filter(a => a.edits.length === 0).length;
  const asDraftedPct = approvals.length
    ? Math.round((approvedAsDrafted / approvals.length) * 1000) / 10
    : 0;
  const approvalsByWorkflow = (() => {
    const rows: Record<string, { label: string; total: number; edited: number }> = {};
    for (const a of approvals) {
      const label = PROXY_WORKFLOW_REGISTRY[a.workflowType].label;
      const row = (rows[label] ??= { label, total: 0, edited: 0 });
      row.total++;
      if (a.edits.length) row.edited++;
    }
    return Object.values(rows)
      .map(r => ({ ...r, pct: Math.round(((r.total - r.edited) / r.total) * 1000) / 10 }))
      .sort((a, b) => a.pct - b.pct || b.total - a.total);
  })();
  const editCounts = (() => {
    const counts: Record<string, number> = {};
    approvals.forEach(a => a.edits.forEach(e => { counts[e] = (counts[e] || 0) + 1; }));
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  })();
  const maxEditCount = editCounts[0]?.[1] ?? 0;
  const recentEdited = approvals
    .filter(a => a.edits.length)
    .sort((a, b) => b.at.localeCompare(a.at))
    .slice(0, 5);

  // Every completed follow-up action (across every registered workflow) is a
  // real, dated record on some job's followUpHistory — bucket by day for an
  // honest "activity over time" trend. No accuracy-over-time equivalent
  // exists for Proxy (no per-task accuracy score, only a confidence at
  // creation time), so this shows volume, same as Accuracy mode's own
  // "Volume Performance" bar.
  const proxyTrend = (() => {
    const counts: Record<string, number> = {};
    for (const job of proxyCtx.jobs) {
      for (const r of job.followUpHistory) {
        const day = r.timestamp.slice(0, 10);
        counts[day] = (counts[day] || 0) + 1;
      }
    }
    return Object.entries(counts)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, Actions]) => ({ date: fmtDate(date), Actions }));
  })();

  const fieldCatLabel = fieldCat === 'all'
    ? 'Combined — Proxy, Corp Action, Regulatory + Issuer'
    : `${CATEGORY_LABEL[fieldCat]} scans only`;
  const selectedBreakdown = breakdown && combinedBreakdown
    ? (fieldCat === 'all' ? combinedBreakdown : breakdown[BREAKDOWN_KEY[fieldCat]])
    : null;

  return (
    <div className="h-full flex flex-col overflow-auto bg-surface">

      {/* ── Dashboard mode toggle ── */}
      <div className="px-6 pt-4 pb-2 bg-white border-b border-trim flex-shrink-0">
        <PillGroup
          options={[{ label: 'Fulfillment Governance', value: 'accuracy' as DashboardMode }, { label: 'Proxy Governance', value: 'proxy' as DashboardMode }]}
          value={mode}
          onChange={(v) => setMode(v as DashboardMode)}
        />
      </div>

      {mode === 'proxy' && (
        <div className="flex-1 flex flex-col overflow-auto">
          {/* ── KPI cards — same StatCard component as Accuracy mode ── */}
          <div className="flex flex-wrap gap-3 px-6 py-5 border-b border-trim bg-white flex-shrink-0">
            <StatCard label="Inbound Emails"
              value={inboundThreads.length} sub={`${aceClassified.length} classified by Ace`} />
            <StatCard label="Classification Accuracy"
              value={`${classificationPct}%`}
              sub={`${aceClassified.length - changedByPerson.length} of ${aceClassified.length} kept as Ace classified them`}
              accent={fieldBarColor(classificationPct)} />
            <StatCard label="Unclassified"
              value={unclassified.length}
              sub={`no rule matched · ${triagedByPerson.length} triaged by a person`}
              accent={unclassified.length > triagedByPerson.length ? MID_COLOR : undefined} />
            <StatCard label="Approved as Drafted"
              value={`${asDraftedPct}%`}
              sub={`${approvedAsDrafted} of ${approvals.length} approvals with no changes`}
              accent={fieldBarColor(asDraftedPct)} />
            <StatCard label="Ready to Approve"
              value={taskBuckets.ready}
              sub={`of ${proxyTasks.length} tasks Ace prepared`} />
            <StatCard label="Escalated to Manager"
              value={escalatedTotal}
              sub={`${taskBuckets.escalatedByAce} by Ace (SOP) · ${taskBuckets.escalatedByPerson} by a person`} />
          </div>

          <div className="flex-1 px-6 py-6 flex flex-col gap-6">
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              {/* ── Accuracy by the category Ace chose ── */}
              <div className="bg-white border border-trim rounded-xl p-5 flex flex-col gap-3">
                <div>
                  <h2 className="text-sm font-semibold text-navy">Classification Accuracy by Category</h2>
                  <p className="text-xs text-muted mt-0.5">Grouped by the category Ace chose. An email counts as wrong when a person changed it. Lowest first.</p>
                </div>
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-trim">
                      <th className="text-left py-2 font-medium text-muted">Ace's category</th>
                      <th className="text-right py-2 font-medium text-muted w-16">Emails</th>
                      <th className="text-right py-2 font-medium text-muted w-20">Changed</th>
                      <th className="text-left py-2 pl-4 font-medium text-muted w-40">Accuracy</th>
                    </tr>
                  </thead>
                  <tbody>
                    {accuracyByCategory.map(r => (
                      <tr key={r.category} className="border-b border-trim last:border-0">
                        <td className="py-2 text-copy">{EMAIL_CATEGORY_LABEL[r.category]}</td>
                        <td className="py-2 text-right text-muted">{r.total}</td>
                        <td className="py-2 text-right" style={{ color: r.changed ? LOW_COLOR : undefined }}>{r.changed || '—'}</td>
                        <td className="py-2 pl-4">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-1.5 rounded-full bg-surface overflow-hidden">
                              <div className="h-full rounded-full" style={{ width: `${r.pct}%`, background: fieldBarColor(r.pct) }} />
                            </div>
                            <span className="w-10 text-right text-navy font-medium">{r.pct}%</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* ── Where a person stepped in ── */}
              <div className="bg-white border border-trim rounded-xl p-5 flex flex-col gap-4">
                <div>
                  <h2 className="text-sm font-semibold text-navy">Where Ace Needed a Person</h2>
                  <p className="text-xs text-muted mt-0.5">Every open task Ace prepared. Nothing is sent until a person approves it.</p>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { label: 'Ready to approve', value: taskBuckets.ready, sub: 'drafted, waiting on sign-off', color: HIGH_COLOR },
                    { label: 'Exceptions', value: taskBuckets.exceptionTotal, sub: "Ace can't act until fixed", color: MID_COLOR },
                    { label: 'Escalated', value: escalatedTotal, sub: 'decision for the ICT manager', color: LOW_COLOR },
                  ].map(b => (
                    <div key={b.label} className="rounded-lg border border-trim px-3 py-2.5">
                      <div className="flex items-center gap-1.5 text-xs text-muted">
                        <span className="w-2 h-2 rounded-full" style={{ background: b.color }} />{b.label}
                      </div>
                      <div className="text-xl font-semibold text-navy mt-0.5">{b.value}</div>
                      <div className="text-xs text-muted">{b.sub}</div>
                    </div>
                  ))}
                </div>
                {taskBuckets.exceptionRows.length > 0 && (
                  <div className="flex flex-col gap-2">
                    <span className="text-xs font-medium text-muted uppercase tracking-wide">Exceptions by type</span>
                    {taskBuckets.exceptionRows.map(([label, count]) => (
                      <div key={label} className="flex items-center gap-3 text-xs">
                        <span className="w-36 text-copy">{label}</span>
                        <div className="flex-1 h-1.5 rounded-full bg-surface overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${(count / taskBuckets.exceptionTotal) * 100}%`, background: MID_COLOR }} />
                        </div>
                        <span className="w-6 text-right text-navy font-medium">{count}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* ── How often a person changed Ace's draft before approving ── */}
            <div className="bg-white border border-trim rounded-xl p-5 flex flex-col gap-4">
              <div>
                <h2 className="text-sm font-semibold text-navy">Draft Approvals</h2>
                <p className="text-xs text-muted mt-0.5">Every task a person approved, and whether they changed anything Ace prepared first. Lowest first.</p>
              </div>
              {approvals.length === 0 ? (
                <div className="text-sm text-muted py-8 text-center">No approvals yet.</div>
              ) : (
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                  <table className="w-full text-xs self-start">
                    <thead>
                      <tr className="border-b border-trim">
                        <th className="text-left py-2 font-medium text-muted">Workflow</th>
                        <th className="text-right py-2 font-medium text-muted w-20">Approved</th>
                        <th className="text-right py-2 font-medium text-muted w-16">Edited</th>
                        <th className="text-left py-2 pl-4 font-medium text-muted w-40">As drafted</th>
                      </tr>
                    </thead>
                    <tbody>
                      {approvalsByWorkflow.map(r => (
                        <tr key={r.label} className="border-b border-trim last:border-0">
                          <td className="py-2 text-copy">{r.label}</td>
                          <td className="py-2 text-right text-muted">{r.total}</td>
                          <td className="py-2 text-right" style={{ color: r.edited ? MID_COLOR : undefined }}>{r.edited || '—'}</td>
                          <td className="py-2 pl-4">
                            <div className="flex items-center gap-2">
                              <div className="flex-1 h-1.5 rounded-full bg-surface overflow-hidden">
                                <div className="h-full rounded-full" style={{ width: `${r.pct}%`, background: fieldBarColor(r.pct) }} />
                              </div>
                              <span className="w-10 text-right text-navy font-medium">{r.pct}%</span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  <div className="flex flex-col gap-4">
                    <div className="flex flex-col gap-2">
                      <span className="text-xs font-medium text-muted uppercase tracking-wide">What people change</span>
                      {editCounts.length === 0 ? (
                        <span className="text-xs text-muted">Nothing yet. Every approval went out as Ace drafted it.</span>
                      ) : editCounts.map(([label, count]) => (
                        <div key={label} className="flex items-center gap-3 text-xs">
                          <span className="w-36 text-copy">{label}</span>
                          <div className="flex-1 h-1.5 rounded-full bg-surface overflow-hidden">
                            <div className="h-full rounded-full" style={{ width: `${(count / maxEditCount) * 100}%`, background: MID_COLOR }} />
                          </div>
                          <span className="w-6 text-right text-navy font-medium">{count}</span>
                        </div>
                      ))}
                    </div>
                    {recentEdited.length > 0 && (
                      <div className="flex flex-col gap-1.5">
                        <span className="text-xs font-medium text-muted uppercase tracking-wide">Recent edited approvals</span>
                        {recentEdited.map(a => (
                          <div key={`${a.taskId}-${a.at}`} className="flex items-baseline justify-between gap-3 text-xs border-b border-trim last:border-0 pb-1.5">
                            <div className="min-w-0">
                              <div className="text-copy truncate">{a.title}</div>
                              <div className="text-muted">Changed {a.edits.join(', ')}</div>
                            </div>
                            <div className="text-muted whitespace-nowrap text-right">{a.at}<br />{a.by}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* ── Audit trail of every category change ── */}
            <div className="bg-white border border-trim rounded-xl overflow-hidden">
              <div className="px-5 py-3 border-b border-trim">
                <h3 className="text-sm font-semibold text-navy">Category Changes</h3>
                <p className="text-xs text-muted mt-0.5">Every time a person changed or filled in Ace's category, newest first. Change a category from the email in Email Inbox.</p>
              </div>
              {corrections.length === 0 ? (
                <div className="text-sm text-muted py-8 text-center">No category changes yet.</div>
              ) : (
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-trim bg-surface">
                      <th className="text-left px-4 py-2.5 font-medium text-muted w-32">When</th>
                      <th className="text-left px-4 py-2.5 font-medium text-muted">Email</th>
                      <th className="text-left px-4 py-2.5 font-medium text-muted w-40">Ace said</th>
                      <th className="text-left px-4 py-2.5 font-medium text-muted w-40">Changed to</th>
                      <th className="text-left px-4 py-2.5 font-medium text-muted w-32">By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {corrections.map((t, i) => (
                      <tr key={t.id} className={i % 2 === 0 ? '' : 'bg-surface/50'}>
                        <td className="px-4 py-2.5 text-muted whitespace-nowrap">{t.correction!.at}</td>
                        <td className="px-4 py-2.5">
                          <button
                            onClick={() => navigate(`/email-inbox?emailId=${leadMessage(t)?.id}&open=1`)}
                            className="text-[#2563EB] hover:underline text-left truncate max-w-[420px] block"
                            title={t.subject}
                          >
                            {t.subject}
                          </button>
                        </td>
                        <td className="px-4 py-2.5 text-muted">{t.correction!.from ? EMAIL_CATEGORY_LABEL[t.correction!.from] : 'Unclassified'}</td>
                        <td className="px-4 py-2.5 text-navy font-medium">{EMAIL_CATEGORY_LABEL[t.correction!.to]}</td>
                        <td className="px-4 py-2.5 text-copy">{t.correction!.by}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="bg-white border border-trim rounded-xl p-5 flex flex-col gap-3">
              <h2 className="text-sm font-semibold text-navy">Follow-Up Activity Over Time</h2>
              <p className="text-xs text-muted -mt-2">Completed follow-up actions logged per day, across every registered Proxy workflow.</p>
              {proxyTrend.length === 0 ? (
                <div className="text-sm text-muted py-8 text-center">No follow-up activity logged yet.</div>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={proxyTrend} margin={{ top: 4, right: 20, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="Actions" fill={VOL_COLOR} radius={[3, 3, 0, 0]} maxBarSize={48} name="Actions Logged" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>
      )}

      {mode === 'accuracy' && (
      <>
      {/* ── KPI cards ── */}
      <div className="flex flex-wrap gap-3 px-6 py-5 border-b border-trim bg-white flex-shrink-0">
        <StatCard label="Work Done"
          value={loading ? '—' : summary.total_receipts.toLocaleString()} sub="receipts processed this period" />
        <StatCard label="Average Speed"
          value={loading ? '—' : 'Not yet tracked'} sub="no timing instrumentation yet" />
        <StatCard label="Average Accuracy"
          value={loading ? '—' : `${summary.overall_avg_score_pct}%`} sub="across all receipts" />
        <StatCard label="Fully Automated"
          value={loading ? '—' : `${fullyAutomatedPct}%`}
          sub="of receipts with 100% AI accuracy" accent={HIGH_COLOR} />
        <StatCard label="Average Cost"
          value={loading ? '—' : 'Not yet tracked'} sub="no cost instrumentation yet" />
      </div>

      {!loading && isMock && (
        <div className="px-6 py-2 bg-amber-50 border-b border-amber-200 text-xs font-medium text-amber-800 flex-shrink-0">
          Sample data — no extraction activity recorded yet. Numbers below are illustrative, not measured.
        </div>
      )}

      <div className="flex-1 px-6 py-6 flex flex-col gap-6">
        {loading && <div className="flex items-center justify-center h-64 text-muted text-sm">Loading…</div>}

        {!loading && (
          <>
            {/* ── Filters + export ── */}
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted font-medium">Period:</span>
                <div className="flex items-center gap-1 bg-surface border border-trim rounded-lg p-0.5">
                  {(['day', 'week', 'month'] as PeriodMode[]).map(mode => (
                    <button
                      key={mode}
                      onClick={() => setPeriodPreset(mode)}
                      className={`px-3 py-1 rounded-md text-xs font-medium capitalize transition-all ${
                        periodMode === mode
                          ? 'bg-white text-navy shadow-sm border border-trim'
                          : 'text-muted hover:text-copy'
                      }`}
                    >
                      {mode}
                    </button>
                  ))}
                </div>
                <span className="text-xs text-muted">or</span>
                <DatePicker
                  selectsRange
                  startDate={dateFrom ? new Date(dateFrom + 'T00:00:00') : null}
                  endDate={dateTo ? new Date(dateTo + 'T00:00:00') : null}
                  onChange={(update: [Date | null, Date | null]) => {
                    const [start, end] = update;
                    setPeriodMode('custom');
                    setDateFrom(start ? toLocalIso(start) : null);
                    setDateTo(end ? toLocalIso(end) : null);
                  }}
                  minDate={new Date('2026-07-01')}
                  placeholderText="Select date range"
                  dateFormat="MMM d, yyyy"
                  isClearable
                  monthsShown={1}
                  className="px-3 py-1.5 text-xs text-navy border border-trim rounded-lg bg-white w-52 cursor-pointer"
                />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted font-medium">Category:</span>
                <PillGroup
                  options={CATEGORY_OPTIONS}
                  value={fieldCat}
                  onChange={(v) => setFieldCat(v as FieldCat)}
                />
                <button
                  onClick={handleExport}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-navy border border-trim rounded-lg bg-white hover:bg-surface transition-colors shadow-sm"
                >
                  <Download size={13} />
                  Export Report
                </button>
                <button
                  onClick={handleExportCsv}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-navy border border-trim rounded-lg bg-white hover:bg-surface transition-colors shadow-sm"
                >
                  <Download size={13} />
                  Export CSV
                </button>
              </div>
            </div>

            {/* ── Category breakdown card(s) ── */}
            {selectedBreakdown && (
              <div className="flex gap-4">
                <CategoryCard label={fieldCat === 'all' ? 'All Categories' : `${CATEGORY_LABEL[fieldCat]} Scans`}
                  total={selectedBreakdown.total} avgPct={selectedBreakdown.avg_score_pct}
                  high={selectedBreakdown.high} mid={selectedBreakdown.mid} low={selectedBreakdown.low} />
              </div>
            )}

            {/* ── Trend charts (always full timeline) ── */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              <div className="bg-white border border-trim rounded-xl p-5 flex flex-col gap-3">
                <h2 className="text-sm font-semibold text-navy">Volume Performance</h2>
                <ResponsiveContainer width="100%" height={220}>
                  <ComposedChart data={chartData} margin={{ top: 4, right: 20, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis yAxisId="vol" orientation="left" tick={{ fontSize: 11 }} label={{ value: 'Materials', angle: -90, position: 'insideLeft', style: { fontSize: 10 } }} />
                    <YAxis yAxisId="acc" orientation="right" domain={[60, 101]} tick={{ fontSize: 11 }} label={{ value: 'Accuracy %', angle: 90, position: 'insideRight', style: { fontSize: 10 } }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar yAxisId="vol" dataKey="Volume" fill={VOL_COLOR} radius={[3, 3, 0, 0]} maxBarSize={48} name="Materials/Week" />
                    <Line yAxisId="acc" type="monotone" dataKey="Accuracy %" stroke={ACC_COLOR} strokeWidth={2} dot={{ r: 4 }} name="Accuracy %" />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-white border border-trim rounded-xl p-5 flex flex-col gap-3">
                <h2 className="text-sm font-semibold text-navy">Accuracy Distribution</h2>
                <div className="flex items-center gap-4 text-xs text-muted">
                  <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm" style={{ background: LOW_COLOR }} />Low (&lt;80%)</span>
                  <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm" style={{ background: MID_COLOR }} />Mid (80–99%)</span>
                  <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm" style={{ background: HIGH_COLOR }} />High (100%)</span>
                </div>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="Low"  stackId="a" fill={LOW_COLOR}  maxBarSize={48} name="Low (<80%)" />
                    <Bar dataKey="Mid"  stackId="a" fill={MID_COLOR}  maxBarSize={48} name="Mid (80-99%)" />
                    <Bar dataKey="High" stackId="a" fill={HIGH_COLOR} maxBarSize={48} radius={[3, 3, 0, 0]} name="High (100%)" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* ── Field-Level Accuracy ── */}
            {fieldAcc.length > 0 && (
              <div className="bg-white border border-trim rounded-xl p-5 flex flex-col gap-3">
                <div>
                  <h2 className="text-sm font-semibold text-navy">Field-Level Accuracy</h2>
                  <p className="text-xs text-muted mt-0.5">{fieldCatLabel} · fields at top need the most attention</p>
                </div>
                <ResponsiveContainer width="100%" height={Math.max(fieldAcc.length * 34, 160)}>
                  <BarChart layout="vertical" data={fieldAcc} margin={{ top: 4, right: 64, left: 112, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                    <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11 }} tickFormatter={(v: number) => `${v}%`} />
                    <YAxis type="category" dataKey="label" tick={{ fontSize: 11 }} width={108} />
                    <Tooltip formatter={(val: number, _: string, props: any) => [`${val}% (${props.payload.total} scans)`, 'Accuracy']} />
                    <Bar dataKey="correct_pct" maxBarSize={20} radius={[0, 3, 3, 0]} name="Accuracy">
                      {fieldAcc.map((_, i) => <Cell key={i} fill={fieldBarColor(fieldAcc[i].correct_pct)} />)}
                      <LabelList dataKey="correct_pct" position="right" formatter={(v: number) => `${v}%`} style={{ fontSize: 11, fill: '#6b7280' }} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* ── Per-scan detail (tabbed, week-filtered) ── */}
            <ScanDetailPanel scans={isMock ? categoryScans : []} weekLabel={periodLabel} />
          </>
        )}
      </div>
      </>
      )}
    </div>
  );
}
